import * as React from 'react';
import { useRef, useState, useEffect } from 'react';
import type { CSSProperties, FC, RefObject } from 'react';
import useFullscreen from 'app/CasinoGames/useFullscreen';
import Launcher from 'app/CasinoGameLauncher';
import { Game } from 'app/CasinoGames/types';
import { GameStarter } from '../useGameStarter';
import Controls from './Controls';
import Details from './Details';
import { useAllLocales, useLocales } from '../locales';

type PlayPageProps = {
  gameStarter: GameStarter;
  screenRef: RefObject<HTMLDivElement>;
  playerPresent: boolean;
  cashbackEnabled: boolean;
};

// Stands in for the game until the visitor asks for it (GameStarter#deferred). Server-rendered
// disabled: on a slow connection a click before hydration would do nothing, so it looks inactive
// until React takes over.
const PlayButton: FC<{ gameStarter: GameStarter }> = ({ gameStarter }) => {
  const locales = useLocales();
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => setHydrated(true), []);

  return (
    <div className="xo-game-screen_play">
      <button type="button" className="xo-button xo-button_green" onClick={gameStarter.play} disabled={!hydrated}>
        {locales('play')}
      </button>
    </div>
  );
};

// A plain link: it works before hydration too.
const LoginToPlayLink: FC = () => {
  const locales = useLocales();

  return (
    <div className="xo-game-screen_play">
      <a className="xo-button xo-button_green" href="/login">
        {locales('login_to_play')}
      </a>
    </div>
  );
};

const DeferredGame: FC<{ gameStarter: GameStarter }> = ({ gameStarter }) =>
  gameStarter.loginRequired ? <LoginToPlayLink /> : <PlayButton gameStarter={gameStarter} />;

const PlayPage: FC<PlayPageProps> = ({ gameStarter, screenRef, playerPresent, cashbackEnabled }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const fullscreen = useFullscreen(containerRef);
  const game = gameStarter.game as Game;
  const locales = useAllLocales();
  // A game with a size of its own gets a screen of that shape, any other the default one (_game.scss).
  const windowStyle = game.windowSizeConfigured
    ? ({ '--xo-game-ratio': game.windowSize.width / game.windowSize.height } as CSSProperties)
    : undefined;

  return (
    <div>
      <div className="xo-game-window" style={windowStyle} ref={containerRef}>
        <div className="xo-game-screen-wrapper" ref={screenRef}>
          <div className="xo-game-screen">
            {gameStarter.deferred ? (
              <DeferredGame gameStarter={gameStarter} />
            ) : (
              <Launcher
                game={game}
                // XO runs games inline on mobile too, never redirecting to the launch URL (rs-1900).
                mobileVisitor={false}
                playerPresent={playerPresent}
                playMode={gameStarter.playMode}
                jackpotAggregatorCampaignViews={[]}
                loadingComponent={<div>Loading</div>}
                locales={locales}
              />
            )}
          </div>
        </div>
        <Controls fullscreen={fullscreen} gameStarter={gameStarter} playerPresent={playerPresent} />
      </div>
      <Details game={game} cashbackEnabled={cashbackEnabled} />
    </div>
  );
};

export default PlayPage;
